import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ProductNutritionalInfoForm from "../forms/productNutritionalInfoForm";
import { formatOnlyDate } from "../../../utils/DateUtils";
import ProductEditFormStep1 from "../forms/productEditFormStep1";
import ProductEditFormStep2 from "../forms/productEditFormStep2";
import Stepper from "../../../components/lib/stepperHeader";

export const STEP = {
    BASIC: 0,
    DETAILS: 1,
    NUTRITION: 2,
    SUMMARY: 3,
};

const steps = [
    { key: STEP.BASIC, label: "Básico" },
    { key: STEP.DETAILS, label: "Detalhes" },
    { key: STEP.NUTRITION, label: "Tabela Nutricional" },
    { key: STEP.SUMMARY, label: "Resumo" },
];

const Info = ({ label, value }) => (
    <div className="flex justify-between border-b pb-1">
        <span className="font-medium">{label}</span>
        <span>{value || "-"}</span>
    </div>
);

const defaultNutrition = [
    { nutrient: "Valor Energético", valueInPortion: "", dailyValue: "" },
    { nutrient: "Carboidratos", valueInPortion: "", dailyValue: "" },
    { nutrient: "Proteínas", valueInPortion: "", dailyValue: "" },
    { nutrient: "Gorduras Totais", valueInPortion: "", dailyValue: "" },
    { nutrient: "Fibra Alimentar", valueInPortion: "", dailyValue: "" },
    { nutrient: "Sódio", valueInPortion: "", dailyValue: "" },
];

const parseNutritionalFacts = (data) => {
    if (!data) return defaultNutrition;
    try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        return (Array.isArray(parsed) && parsed.length > 0) ? parsed : defaultNutrition;
    } catch (e) {
        return defaultNutrition;
    }
};

const ProductEditStepper = ({ product, initialImagePreview, onFinish, onCancel, onDirty }) => {
    const [step, setStep] = useState(STEP.BASIC);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            imageFile: null,
            imagePreview: initialImagePreview || null,

            name: product?.name || "",
            barcode: product?.barcode || "",
            brand: product?.brand || "",
            quantity: product?.quantity ?? "",
            price: product?.price ?? "",
            online_price: product?.online_price ?? "",
            fabrication_date: product?.fabrication_date || "",
            expiration_date: product?.expiration_date || "",
            sku: product?.sku || "",
            limit_warning: product?.limit_warning ?? "",
            category: product?.category || "",
            description: product?.description || "",
            observations: product?.observations || "",
            active: product?.active ?? false,
            needsPreparation: product?.needsPreparation ?? false,
            nutrition: parseNutritionalFacts(product?.nutritional_facts),
            clientCode: product?.clientCode
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required("Nome é um campo obrigatório")
                .max(50, "O nome deve ter no máximo 50 caracteres"),
            quantity: Yup.number()
                .required("Quantidade é um campo obrigatório")
                .typeError("A quantidade deve ser um número"),
            price: Yup.number()
                .required("Preço é um campo obrigatório")
                .typeError("O preço deve ser um número"),
            imageFile: Yup.mixed()
                .nullable()
                .test("fileSize", "A foto deve ter no máximo 25MB", (value) => {
                    if (!value) return true;
                    return value.size <= 25 * 1024 * 1024;
                })
                .test("fileType", "Formato inválido. Use JPEG, JPG ou PNG", (value) => {
                    if (!value) return true;
                    return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
                }),
        }),
        onSubmit: async (values) => {
            if (step < STEP.SUMMARY) {
                setStep(s => s + 1);
                return;
            }

            const { imageFile, imagePreview, nutrition, ...productData } = values;

            await onFinish({
                product: productData,
                nutritional_facts: JSON.stringify(nutrition),
                image: imageFile,
            });
        },
    });

    useEffect(() => {
        if (initialImagePreview && !formik.values.imagePreview) {
            formik.setFieldValue("imagePreview", initialImagePreview);
        }
    }, [initialImagePreview]);

    return (
        <form onSubmit={formik.handleSubmit} onChange={onDirty} className="flex flex-col h-full">
            <Stepper
                steps={steps}
                currentStep={step}
            />

            <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
                {step === STEP.BASIC && <ProductEditFormStep1 formik={formik} />}
                {step === STEP.DETAILS && <ProductEditFormStep2 formik={formik} />}
                {step === STEP.NUTRITION && <ProductNutritionalInfoForm formik={formik} />}

                {step === STEP.SUMMARY && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                        <div className="space-y-6">
                            <div className="w-full aspect-video md:aspect-square lg:aspect-video bg-gray-50 rounded-xl overflow-hidden border border-gray-200 shadow-sm relative flex items-center justify-center">
                                {formik.values.imagePreview ? (
                                    <img
                                        src={formik.values.imagePreview}
                                        className="w-full h-full object-contain"
                                        alt="Preview"
                                    />
                                ) : (
                                    <span className="text-gray-400 text-sm">Sem imagem</span>
                                )}
                            </div>

                            <div className="bg-gray-50 p-4 rounded-xl space-y-3 border border-gray-200">
                                <h4 className="font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-2">Informações Básicas</h4>
                                <Info label="Nome" value={formik.values.name} />
                                <Info label="Cód. Barras" value={formik.values.barcode} />
                                <Info label="Marca" value={formik.values.brand} />
                                <Info label="Preço" value={formik.values.price ? `R$ ${formik.values.price}` : ""} />
                                <Info label="Preço Online" value={formik.values.online_price ? `R$ ${formik.values.online_price}` : ""} />
                                <Info label="Categoria" value={formik.values.category} />
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="bg-gray-50 p-4 rounded-xl space-y-3 border border-gray-200">
                                <h4 className="font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-2">Detalhes & Estoque</h4>
                                <Info label="Estoque Atual" value={formik.values.quantity} />
                                <Info label="Fabricação" value={formatOnlyDate(formik.values.fabrication_date)} />
                                <Info label="Validade" value={formatOnlyDate(formik.values.expiration_date)} />
                                <Info label="SKU" value={formik.values.sku} />
                                <Info label="Aviso Limite" value={formik.values.limit_warning} />
                                <div className="grid grid-cols-2 gap-4 pt-2">
                                    <div className="text-center p-2 bg-white rounded border">
                                        <span className="block text-xs text-gray-500">Ativo</span>
                                        <span className={`font-medium ${formik.values.active ? "text-green-600" : "text-red-500"}`}>
                                            {formik.values.active ? "Sim" : "Não"}
                                        </span>
                                    </div>
                                    <div className="text-center p-2 bg-white rounded border">
                                        <span className="block text-xs text-gray-500">Preparo</span>
                                        <span className="font-medium text-gray-700">
                                            {formik.values.needsPreparation ? "Sim" : "Não"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <span className="text-sm font-medium text-gray-700 block mb-1">Descrição</span>
                                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200 min-h-[60px]">
                                        {formik.values.description || <span className="italic text-gray-400">Nenhuma descrição</span>}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 block mb-1">Observações</span>
                                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200 min-h-[60px]">
                                        {formik.values.observations || <span className="italic text-gray-400">Nenhuma observação</span>}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="col-span-1 md:col-span-2 mt-2">
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                                    <h4 className="font-semibold text-gray-700">Tabela Nutricional</h4>
                                </div>
                                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {formik.values.nutrition.map((row, index) => (
                                        <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded border border-gray-100">
                                            <span className="text-sm font-medium text-gray-600">{row.nutrient}</span>
                                            <span className="text-sm font-bold text-gray-800">
                                                {row.valueInPortion || "-"}
                                                <span className="text-xs font-normal text-gray-500 ml-1">
                                                    ({row.dailyValue || "0"}%)
                                                </span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center bg-white sticky bottom-0 z-10">
                <button
                    type="button"
                    onClick={step === STEP.BASIC ? onCancel : () => setStep(step - 1)}
                    className="px-4 py-2 rounded-xl border hover:bg-gray-50 transition"
                >
                    {step === STEP.BASIC ? "Cancelar" : "Voltar"}
                </button>

                <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="px-8 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                    {formik.isSubmitting ? (
                        <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processando...
                        </span>
                    ) : (
                        step === STEP.SUMMARY ? "Concluir" : "Avançar"
                    )}
                </button>
            </div>
        </form>
    );
};

export default ProductEditStepper;